import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useGetAllSessionsQuery } from '@/core/services/medconnect-agent-service'

interface UseAgentChatSessionOptions {
    /** Base path for navigation (e.g., '/medconnect-agent' or '/medconnect-admin-agent') */
    basePath: string
    /** Optional agent type to filter sessions (e.g., 'admin', 'patient-consultation') */
    agentType?: string
}

/**
 * Custom hook for managing agent chat session state and navigation.
 * Encapsulates session selection, URL sync, auto-refresh, and drawer state.
 */
export function useAgentChatSession({ basePath, agentType }: UseAgentChatSessionOptions) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { sessionId: activeSessionIdFromUrl } = useParams<{ sessionId?: string }>()

    const [selectedSessionId, setSelectedSessionId] = useState<string | null | undefined>(activeSessionIdFromUrl)
    const [isConfigPanelOpen, setConfigPanelOpen] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [hasMessageBeenSent, setHasMessageBeenSent] = useState(false)

    const {
        data: sessions,
        isLoading: isLoadingSessions,
        error: sessionsError,
        refetch: refetchSessions,
    } = useGetAllSessionsQuery(agentType)

    // Filter sessions by agentType
    const filteredSessions = agentType 
        ? sessions?.filter((session) => session.agentType === agentType)
        : sessions

    // Derive title from current session
    const currentSessionInfo = filteredSessions?.find((session) => session.sessionId === selectedSessionId)
    const title = currentSessionInfo?.title || t('page.medconnectAgent.chatHistory.newChat')

    // Sync selected session with URL param
    useEffect(() => {
        setSelectedSessionId(activeSessionIdFromUrl)
    }, [activeSessionIdFromUrl])

    // Refresh sessions every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            refetchSessions()
        }, 30000)
        return () => clearInterval(interval)
    }, [refetchSessions])

    // Refresh sessions after first message is sent
    useEffect(() => {
        if (hasMessageBeenSent) {
            refetchSessions()
        }
    }, [hasMessageBeenSent, refetchSessions])

    const handleDrawerToggle = useCallback(() => {
        setDrawerOpen((prev) => !prev)
    }, [])

    const handleSessionSelect = useCallback(
        (sessionId: string) => {
            setSelectedSessionId(sessionId)
            navigate(`${basePath}/${sessionId}`)
            setDrawerOpen(false)
        },
        [basePath, navigate]
    )

    const handleNewChat = useCallback(() => {
        setSelectedSessionId(null)
        navigate(basePath)
        setDrawerOpen(false)
    }, [basePath, navigate])

    // Handler for session ID changes from ChatInterface (updates URL without reload)
    const handleSessionIdChange = useCallback(
        (sessionId: string) => {
            if (sessionId && (!selectedSessionId || selectedSessionId !== sessionId)) {
                setSelectedSessionId(sessionId)
                const newUrl = `${basePath}/${sessionId}`
                window.history.replaceState({ path: newUrl }, '', newUrl)
            }
        },
        [basePath, selectedSessionId]
    )

    // Handler for when a message is sent (triggers session refresh after first message)
    const handleMessageSent = useCallback(() => {
        if (!hasMessageBeenSent) {
            setHasMessageBeenSent(true)
        }
    }, [hasMessageBeenSent])

    const toggleConfigPanel = useCallback((open?: boolean) => {
        setConfigPanelOpen((prev) => (open !== undefined ? open : !prev))
    }, [])

    return {
        // Session state
        selectedSessionId,
        sessions: filteredSessions,
        isLoadingSessions,
        sessionsError,
        title,
        // UI state
        drawerOpen,
        isConfigPanelOpen,
        // Handlers - renamed for consistency
        onDrawerToggle: handleDrawerToggle,
        onSessionSelect: handleSessionSelect,
        onNewChat: handleNewChat,
        onSessionIdChange: handleSessionIdChange,
        onMessageSent: handleMessageSent,
        onConfigPanelToggle: toggleConfigPanel,
    }
}
